<template>
  <template v-if="treeNode.visible">
    <scene-element
      v-if="isElementTreeNode(treeNode)"
      :node-uid="treeNode.uid"
      :size="itemSizes[treeNode.uid]"
      :net-mode="canvasProps.mode"
      :editable="mode === 'edit'"
      :selection="selection"
      :tool="netTool.id"
      :disabled="disabledItemUids.has(treeNode.uid)"
      :impact-source-element="impactSourceElement"
      :flow-source-element="flowSourceElement"
      :fixed-item="fixedItem"
      :fade="fadeElements"
      :clickable="!treeNode.superLocked && netTool.type != 'creating'"
      :locked="treeNode.locked"
      @select="emit('select-item', $event)"
      @add-select="emit('add-select-item', $event)"
      @contextmenu="onContextMenu($event)"
      @wheel="emit('wheel', $event)"
      @update:value="emit('update:element-value', $event)"
      @toggle-relation-mode="emit('toggle-relation-mode', $event)"
      @toggle-flow-mode="emit('toggle-flow-mode', $event)"
      @update:tool="emit('update:tool', $event)"
    />

    <scene-text
      v-if="isTextTreeNode(treeNode)"
      :node-uid="treeNode.uid"
      :editable="mode === 'edit'"
      :size="itemSizes[treeNode.uid]"
      :selection="selection"
      :clickable="!treeNode.superLocked && netTool.type != 'creating'"
      :tool="netTool.id"
      :locked="treeNode.locked"
      @select="emit('select-item', $event)"
      @wheel="emit('wheel', $event)"
      @add-select="emit('add-select-item', $event)"
      @contextmenu="onContextMenu($event)"
      @update:tool="emit('update:tool', $event)"
    ></scene-text>

    <scene-medium
      v-if="isMediumTreeNode(treeNode)"
      :node-uid="treeNode.uid"
      :editable="mode === 'edit'"
      :size="itemSizes[treeNode.uid]"
      :selection="selection"
      :clickable="!treeNode.superLocked && netTool.type != 'creating'"
      :locked="treeNode.locked"
      @select="emit('select-item', $event)"
      @wheel="emit('wheel', $event)"
      @add-select="emit('add-select-item', $event)"
      @contextmenu="onContextMenu($event)"
    ></scene-medium>

    <scene-shape
      v-if="isShapeTreeNode(treeNode)"
      :node-uid="treeNode.uid"
      :editable="mode === 'edit'"
      :size="itemSizes[treeNode.uid]"
      :selection="selection"
      :clickable="!treeNode.superLocked && netTool.type != 'creating' && netTool.type != 'dragging'"
      :tool="netTool.id"
      :locked="treeNode.locked"
      @select="emit('select-item', $event)"
      @wheel="emit('wheel', $event)"
      @add-select="emit('add-select-item', $event)"
      @contextmenu="onContextMenu($event)"
      @set-tool="emit('update:tool', $event)"
    ></scene-shape>

    <scene-chart
      v-if="isChartTreeNode(treeNode)"
      :node-uid="treeNode.uid"
      :editable="mode === 'edit'"
      :size="itemSizes[treeNode.uid]"
      :selection="selection"
      :clickable="!treeNode.superLocked && netTool.type != 'creating'"
      :locked="treeNode.locked"
      @select="emit('select-item', $event)"
      @wheel="emit('wheel', $event)"
      @add-select="emit('add-select-item', $event)"
      @contextmenu="onContextMenu($event)"
      @update:tool="emit('update:tool', $event)"
    ></scene-chart>

    <scene-group
      v-if="isGroupTreeNode(treeNode) && mode === 'edit' && flowSourceElement == null && impactSourceElement == null"
      :node-uid="treeNode.uid"
      :selection="selection"
      :size="itemSizes[treeNode.uid]"
    ></scene-group>
    
    <scene-node
      v-for="childNode in treeNode.children"
      :key="childNode.uid"
      :tree-node="childNode"
      :itemSizes="itemSizes"
      @contextmenu="onContextMenu($event)"
      @click="emit('click', $event)"
      @toggle-relation-mode="emit('toggle-relation-mode', $event)"
      @toggle-flow-mode="emit('toggle-flow-mode', $event)"
      @select-item="emit('select-item', $event)"
      @add-select-item="emit('add-select-item', $event)"
      @clear-all-selection="emit('clear-all-selection')"
      @select-many="emit('select-many', $event)"
      @delete="emit('delete')"
      @wheel="emit('wheel', $event)"
      @update:tool="emit('update:tool', $event)"
      @update:element-value="emit('update:element-value', $event)"
    />

  </template>
</template>

<script setup lang="ts">
import { computed, inject, toRefs } from 'vue';
import { keyNetCanvas } from '@editor/net/netCanvasInjectionKeys';

import SceneElement from '../sceneElement/SceneElement.vue';
import SceneShape from '../sceneShape/SceneShape.vue';
import SceneMedium from '../sceneMedium/SceneMedium.vue';
import SceneChart from '../sceneChart/SceneChart.vue';
import SceneText from '../sceneText/SceneText.vue';
import SceneGroup from '../sceneGroup/SceneGroup.vue';

import { netTools } from '@editor/net/NetTools';
import { isElementTreeNode } from '../sceneElement/sceneElementFunctions';
import { isTextTreeNode } from '../sceneText/sceneTextFunctions';
import { isMediumTreeNode } from '../sceneMedium/sceneMediumFunctions';
import { isShapeTreeNode } from '../sceneShape/sceneShapeFunctions';
import { isChartTreeNode } from '../sceneChart/sceneChartFunctions';
import { isGroupTreeNode } from '../sceneGroup/sceneGroupFunctions';

import type { NetCanvasProps } from '@editor/net/NetCanvasProps';
import type { ResourceItem } from '../resource/ResourceItem';
import type { IndexItem } from '../index/IndexItem';
import type { TargetItem } from '../target/TargetItem';
import type { Uid } from '@/helpers/utility/Id';
import type { SceneTreeItemSize, SceneTreeNode } from './SceneTreeItem';

type ElementItem = TargetItem | ResourceItem | IndexItem;
type ContextItemType = "relation" | "sceneNode" | "flow" | "flowBranch";

const props = defineProps<{
  treeNode: SceneTreeNode;
  itemSizes: { [uid: string]: SceneTreeItemSize },
}>();
const emit = defineEmits<{
  (e: "contextmenu", event: MouseEvent & { items?: { uid: Uid<ContextItemType> }[], path: Uid<"sceneNode">[] }): void;
  (e: "click", event: MouseEvent): void;
  (e: "toggle-relation-mode", item: Pick<ElementItem, "type" | "id" | "uid">): void;
  (e: "toggle-flow-mode", item: { uid: Uid<"target" | "resource"> }): void;
  (e: "select-item", payload: { uid: Uid<"sceneNode" | "flow" | "relation" | "flowBranch" | "momentum">; }): void;
  (e: "add-select-item", payload: { uid: Uid<"sceneNode" | "flow" | "relation" | "flowBranch" | "momentum">; }): void;
  (e: "select-many", payload: { uid: Uid<"sceneNode" | "flow" | "relation" | "flowBranch" | "momentum">; }[]): void;
  (e: "clear-all-selection"): void;
  (e: "delete"): void;
  (e: "wheel", event: WheelEvent): void;
  (e: "update:tool", tool: NetCanvasProps["tool"]): void;
  (e: "update:element-value", payload: { uid: Uid<"target" | "resource" | "index">; value: number }): void;
}>();

const injectedCanvas = inject(keyNetCanvas);
const canvasProps = computed((): NetCanvasProps => injectedCanvas?.value.props as NetCanvasProps || {} as NetCanvasProps);
const {
  flowSourceElement,
  impactSourceElement,
  fixedItem,
  selection,
  disabledItemUids,
  mode,
  fadeElements,
  tool
} = toRefs(canvasProps.value);

const netTool = computed(() => {
  return netTools[tool.value];
});

// ================================
// INTERACTION

function onContextMenu(event: MouseEvent & { items?: { uid: Uid<ContextItemType>; nodeUid?: Uid<"sceneNode"> }[], path?: Uid<"sceneNode">[] }) {
  if (!event.path) event.path = [...props.treeNode.path];
  emit("contextmenu", event as MouseEvent & { items?: { uid: Uid<ContextItemType>; nodeUid?: Uid<"sceneNode"> }[], path: Uid<"sceneNode">[] });
}

</script>

<style lang="scss"></style>